import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { FileType } from "@core/config/file-type";
import dayjs from "dayjs";

export function formatarLista(nome: string, dados: any): any {
  return {
    name: nome,
    itens: dados
  };
}

export function aplicarTrim(form: FormGroup): void {
  Object.keys(form.controls).forEach(key => {
    const control = form.get(key);
    if (control && control.value && typeof control.value === 'string') {
      control.setValue(control.value.trim());
    }
  });
}

export function renomearAtributo(objeto: any) {
  if (objeto.hasOwnProperty('itens') && Array.isArray(objeto.itens)) {
    objeto.itens.forEach((item: any) => {
      item.descricao = item.nome;
      delete item.nome;
    });
  }
  return objeto;
}

export function converterItensVaziosEmNull<T>(obj: T): T {
  function converter(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(item => converter(item));
    } else if (obj !== null && typeof obj === 'object') {
      const convertedObj: { [key: string]: any } = {};
      for (const key in obj) {
        if (obj[key] === "") {
          convertedObj[key] = null;
        } else {
          convertedObj[key] = converter(obj[key]);
        }
      }
      return convertedObj;
    }
    return obj;
  }

  return converter(obj);
}

export function separarNumeroCena(numeroCena: any) {
  if (typeof numeroCena !== 'string') {
    numeroCena = numeroCena.toString();
  }

  const match = numeroCena.match(/(\d+)([A-Za-z]+)/);

  if (match) {
    const numCena = match[1];
    const complementoCena = match[2];
    return { numCena, complementoCena };
  } else {
    if (numeroCena.match(/^\d+$/)) {
      return { numCena: numeroCena, complementoCena: '' };
    }

    const numCena = null;
    const complementoCena = null;
    return { numCena, complementoCena };
  }
}

export function isFileTypeAccepted(file: File): boolean {
  const acceptedTypes = [
    ...FileType.XLSX,
    ...FileType.XLS,
    ...FileType.DOC,
    ...FileType.DOCX,
    ...FileType.PDF
  ];

  return acceptedTypes.includes(file.type);
}

export function buildQueryString(params: any): string {
  const queryString = Object.keys(params)
    .map(key => {
      const value = params[key];
      if (value === undefined || value === null || value === '') {
        return '';
      }
      if (Array.isArray(value)) {
        return value
          .filter(item => item !== undefined && item !== null && item !== '')
          .map(item => `${encodeURIComponent(key)}=${encodeURIComponent(item)}`)
          .join('&');
      } else if (typeof value === 'object') {
        return Object.keys(value)
          .map(subKey => `${encodeURIComponent(key)}[${encodeURIComponent(subKey)}]=${encodeURIComponent(value[subKey])}`)
          .join('&');
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .filter(Boolean)
    .join('&');
  return queryString ? `&${queryString}` : '';
}

export function addFormControls(nameForm: FormGroup, controlsConfig: { [key: string]: any[] }, prefix?: string): void {
  for (const key in controlsConfig) {
    if (controlsConfig.hasOwnProperty(key)) {
      const [value, validator] = controlsConfig[key];
      const nameControl = prefix ? prefix + key : key;
      nameForm.addControl(nameControl, new FormControl(value, validator));
    }
  }
}

export function addFormArrayControls(
  formGroup: FormGroup,
  controlsConfig: { [key: string]: any }
): void {
  Object.keys(controlsConfig).forEach(key => {
    const control = controlsConfig[key];

    // Se for um FormArray, adiciona diretamente
    if (control instanceof FormArray) {
      formGroup.setControl(key, control);
    }
    // Se for uma configuração de FormControl ou FormGroup
    else if (Array.isArray(control)) {
      const [value, validator] = control;
      formGroup.addControl(key, new FormControl(value, validator || null));
    }
  });
}


export function ordenarPorNome(lista: any[], campo: string): any[] {
  return lista.sort((a, b) => a[campo].localeCompare(b[campo]));
}


export function getCamposObrigatoriosNaoPreenchidos(formGroup: FormGroup): string[] {
  const camposNaoPreenchidos: string[] = [];

  Object.keys(formGroup.controls).forEach(key => {
    const control = formGroup.get(key);

    if (control?.invalid && control?.errors?.['required']) {
      const elementoDOM = document.querySelector(`[formControlName="${key}"]`);
      const label = elementoDOM?.getAttribute('nzPlaceHolder') ?? '';
      camposNaoPreenchidos.push(label);
    }
  });


  return camposNaoPreenchidos;
}


export function validarCamposObrigatorios(requiredFields: any[], form: FormGroup): void {
  requiredFields.forEach(field => {
    const control = form.get(field);
    if (control) {
      control.setValidators([Validators.required]);
      control.updateValueAndValidity();
    }
  });
}

export function validarCamposObrigatoriosAjuste(requiredFields: any[], form: FormGroup): void {
  requiredFields.forEach(field => {
    const control = form.get(field);
    if (control) {
      const hasRequiredValidator = control.validator && control.validator({} as AbstractControl)?.["required"];

      if (!hasRequiredValidator) {
        control.setValidators([Validators.required]);
        control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      }
    }
  });
}

export function clearCamposObrigatorios(requiredFields: any[], form: FormGroup): void {
  requiredFields.forEach(field => {
    const control = form.get(field);
    if (control) {
      control.clearValidators();
      control.updateValueAndValidity();
    }
  });
}

export function dataComDigitacao(inputValue: any, formGroup: FormGroup, campo: string): void {
  let inputValueString = inputValue?.target?.value || '';
  let cleanedValue = inputValueString.replace(/[^0-9]/g, '');

  if (cleanedValue.length > 2 && cleanedValue.length <= 4) {
    cleanedValue = cleanedValue.replace(/(\d{2})(\d+)/, '$1/$2');
  } else if (cleanedValue.length > 4) {
    cleanedValue = cleanedValue.replace(/(\d{2})(\d{2})(\d+)/, '$1/$2/$3');
  }

  inputValue.target.value = cleanedValue;

  if (cleanedValue.length === 10 && /\d{2}\/\d{2}\/\d{4}/.test(cleanedValue)) {
    const formattedDate = dayjs(cleanedValue, 'DD/MM/YYYY', true);
    if (formattedDate.isValid()) {
      formGroup.get(campo)?.setValue(formattedDate.toISOString());
    }
  }
}

export function formatCNPJ(cnpj: string): string {
  if (!cnpj) return '';
  const cleanedCNPJ = cnpj.replace(/\D/g, '');
  return cleanedCNPJ.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
}

export function formatCPF(cpf: string): string {
  if (!cpf) return '';
  const cleanedCPF = cpf.replace(/\D/g, '');
  return cleanedCPF.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
}

export function removerDuplicados(array: any) {
  const vistos = new Set();
  return array.filter((item: any) => {
      const identificador = JSON.stringify(item); // Serializa o objeto completo como identificador
      if (vistos.has(identificador)) {
          return false; // Se o identificador já foi visto, não inclui no resultado
      }
      vistos.add(identificador); // Adiciona o identificador ao Set
      return true; // Mantém o item no array
  });
}

export function requiredFields(tipo: any, value: any, lista: any) {
  // [class.required]="isOrgao()" no html
  switch (tipo) {
    case value:
      return lista.some((field: any) => field === value);
    default:
      return false;
  }


}

export const areArraysEqual = (arr1: string | any[], arr2: string | any[]): boolean => {
  if (arr1.length !== arr2.length) {
    return false;
  }
  for (let i = 0; i < arr1.length; i++) {
    if (!deepEqual(arr1[i], arr2[i])) {
      return false;
    }
  }
  return true;
};

function deepEqual(obj1: { [x: string]: any; } | null, obj2: { [x: string]: any; } | null): boolean {
  const strObj1 = JSON.stringify(obj1);
  const strObj2 = JSON.stringify(obj2);
  return strObj1 === strObj2;
}

export function trimObjectStrings(obj: any): any {
  if (!obj || typeof obj !== 'object') return obj;
  const trimmed: any = {};
  Object.keys(obj).forEach(key => {
    trimmed[key] = typeof obj[key] === 'string' ? obj[key].trim() : obj[key];
  });
  return trimmed;
}
